import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePricing } from "@/lib/pricing";

type LoginSearch = { deck?: string; next?: string };

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    deck: typeof search.deck === "string" ? search.deck : undefined,
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Sign in - DeckScore" },
      { name: "description", content: "Sign in with a magic link to unlock your full deck analysis." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const pricing = usePricing();
  const { deck, next } = Route.useSearch();
  const isApply = (next ?? "").startsWith("/apply");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setError(null);
    setLoading(true);
    try {
      const nextPath = next && next.startsWith("/") ? next : "/dashboard";
      const url = new URL(nextPath, window.location.origin);
      if (deck) url.searchParams.set("deck", deck);
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: url.toString(), shouldCreateUser: true },
      });
      if (otpError) {
        console.error("signInWithOtp error", otpError);
        const raw = otpError.message || (otpError as unknown as { error_description?: string })?.error_description || "";
        const status = (otpError as unknown as { status?: number })?.status;
        const lowered = raw.toLowerCase();
        const friendly =
          status === 500 || lowered.includes("error sending") || lowered.includes("send") || raw === "";
        setError(
          friendly
            ? "We couldn't send your login email right now - the email service may be misconfigured or rate-limited. Please try again in a minute."
            : raw,
        );
        setLoading(false);
        return;
      }
      setSent(true);
    } catch (err: unknown) {
      const m = err instanceof Error && err.message ? err.message : "Something went wrong. Please try again.";
      setError(m);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col">
      <header className="border-b border-line">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block h-[18px] w-[18px] rounded-md bg-accent" />
            <span className="font-display text-[15px] font-semibold">DeckScore</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 py-12">
        <div className="w-full max-w-[440px] bg-surface border border-line rounded-2xl p-8">
          {!sent ? (
            <>
              <p className="font-script text-accent text-[22px] leading-none">{isApply ? "one quick step" : "unlock your analysis"}</p>
              <h1 className="font-display text-[26px] font-semibold mt-2 mb-2">{isApply ? "Sign in to submit your application" : "Sign in to DeckScore"}</h1>
              <p className="text-[14px] text-ink-secondary mb-6">
                {isApply
                  ? `Confirm your email with a magic link - no password. You'll come straight back to finish your ${pricing.submissionAmountDisplay} submission.`
                  : "We'll email you a magic link - no password needed."}
              </p>
              <form onSubmit={onSubmit} className="flex flex-col gap-3">
                <input
                  type="email"
                  required
                  placeholder="you@startup.com"
                  aria-label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-h-[48px] rounded-xl border border-line bg-bg px-4 text-[15px] text-ink placeholder:text-ink-tertiary focus:border-accent focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex min-h-[48px] items-center justify-center rounded-full bg-accent px-6 text-[15px] font-bold text-accent-ink transition hover:-translate-y-px hover:bg-accent-press disabled:opacity-70"
                >
                  {loading ? "Sending…" : "Send me a magic link"}
                </button>
                {error && <p className="text-[13px] text-[#C65C4E]">{error}</p>}
              </form>
            </>
          ) : (
            <>
              <p className="font-script text-accent text-[22px] leading-none">check your email</p>
              <h1 className="font-display text-[24px] font-semibold mt-2 mb-3">Magic link sent</h1>
              <p className="text-[14px] text-ink-secondary mb-5">
                We sent a magic link to <span className="text-ink font-medium">{email}</span>.{" "}
                {isApply ? `Click it to come back and complete your ${pricing.submissionAmountDisplay} submission.` : "Click it to unlock your full analysis."}
              </p>
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setEmail("");
                }}
                className="text-[13px] text-ink-secondary underline hover:text-ink"
              >
                Use a different email
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
